/* Flow Hair & Beauty — Medical wigs landing page (minimal, dependency-free).
   - Captures UTM / Google click IDs into hidden form fields (sent only to Flow's inbox, never to Google).
   - Validates "phone or email" for the Shopify contact form.
   - Emits privacy-safe analytics events through the store's EXISTING tag setup:
       Shopify.analytics.publish (for a Customer Events custom pixel), window.dataLayer (if GTM exists),
       window.gtag (if a Google tag is already on the page). No tags are loaded by this file.
   - Event payloads never include form values, interest, insurance answers or messages. */
(function () {
  'use strict';

  var ATTR_KEY = 'mwl_attribution';
  var ATTR_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid'];
  var ALLOWED_EVENT_PARAMS = ['page_variant', 'cta_location', 'link_type'];

  function store(fn) { try { return fn(); } catch (e) { return null; } }

  function pageVariant() {
    var el = document.querySelector('[data-mwl-variant]');
    return el ? el.getAttribute('data-mwl-variant') : 'general';
  }

  /* ---------- Analytics ---------- */
  function track(name, params) {
    var clean = { page_variant: pageVariant() };
    if (params) {
      ALLOWED_EVENT_PARAMS.forEach(function (k) { if (params[k]) clean[k] = String(params[k]).slice(0, 60); });
    }
    store(function () { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('mwl_' + name, clean); });
    store(function () { if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: 'mwl_' + name }, clean)); });
    store(function () { if (typeof window.gtag === 'function') window.gtag('event', name === 'lead_submitted' ? 'generate_lead' : 'mwl_' + name, clean); });
  }

  /* ---------- Attribution ---------- */
  function captureAttribution() {
    var params = new URLSearchParams(window.location.search);
    var found = {};
    ATTR_PARAMS.forEach(function (k) { var v = params.get(k); if (v) found[k] = v.slice(0, 150); });
    var saved = store(function () { return JSON.parse(sessionStorage.getItem(ATTR_KEY) || 'null'); }) || {};
    if (Object.keys(found).length) {
      saved = found;
      saved.landing_page = window.location.pathname;
      store(function () { sessionStorage.setItem(ATTR_KEY, JSON.stringify(saved)); });
    }
    return saved;
  }

  function fillAttribution(form, attr) {
    Object.keys(attr).forEach(function (k) {
      var input = form.querySelector('[data-mwl-attr="' + k + '"]');
      if (input) input.value = attr[k];
    });
  }

  /* ---------- Lead form ---------- */
  function setError(form, msg, field) {
    var box = form.querySelector('[data-mwl-error]');
    if (box) { box.textContent = msg; box.hidden = !msg; }
    form.querySelectorAll('[aria-invalid]').forEach(function (el) { el.removeAttribute('aria-invalid'); });
    if (field) { field.setAttribute('aria-invalid', 'true'); field.focus(); }
  }

  function initForm(form, attr) {
    fillAttribution(form, attr);

    var email = form.querySelector('[data-mwl-email]');
    var phone = form.querySelector('[data-mwl-phone]');
    var first = form.querySelector('[data-mwl-first]');
    var last = form.querySelector('[data-mwl-last]');
    var fullName = form.querySelector('[data-mwl-name]');
    var emailFlag = form.querySelector('[data-mwl-email-flag]');
    var fallbackEmail = form.getAttribute('data-mwl-fallback-email');

    // With JavaScript on, email is no longer required by itself: phone OR email is enough.
    if (email && phone && fallbackEmail) email.removeAttribute('required');

    form.addEventListener('submit', function (e) {
      setError(form, '');
      if (first && !first.value.trim()) { e.preventDefault(); setError(form, 'Please enter your first name.', first); return; }
      var hasEmail = email && email.value.trim() !== '';
      var hasPhone = phone && phone.value.replace(/\D/g, '').length >= 7;
      if (!hasEmail && !hasPhone) { e.preventDefault(); setError(form, 'Please share a phone number or an email address so we can reach you.', phone || email); return; }
      if (hasEmail && !email.checkValidity()) { e.preventDefault(); setError(form, 'Please check the email address.', email); return; }

      if (fullName) fullName.value = [first ? first.value.trim() : '', last ? last.value.trim() : ''].join(' ').trim();
      if (!hasEmail && fallbackEmail) {
        // Shopify's contact form requires contact[email]. For phone-only requests we route to Flow's own inbox
        // and flag it, so the team knows to call rather than reply.
        email.value = fallbackEmail;
        if (emailFlag) emailFlag.value = 'No email given — please call';
      } else if (emailFlag) {
        emailFlag.value = 'Yes';
      }
      var btn = form.querySelector('[type="submit"]');
      if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; setTimeout(function () { btn.disabled = false; btn.removeAttribute('aria-busy'); }, 8000); }
    });
  }

  // Primary conversion: fire only when Shopify confirms the submission (success state rendered).
  function handleSuccess() {
    var success = document.querySelector('[data-mwl-success]');
    if (!success) return;
    var url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') === 'true') {
      track('lead_submitted', { cta_location: 'lead_form' });
      url.searchParams.delete('contact_posted');
      store(function () { history.replaceState(null, '', url.pathname + url.search + url.hash); }); // prevents a reload re-counting
    }
    var heading = success.querySelector('h3');
    if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: false }); }
  }

  /* ---------- Prefill from CTAs ---------- */
  function setChoice(name, value) {
    if (!value) return;
    document.querySelectorAll('[data-mwl-choice="' + name + '"]').forEach(function (el) {
      if (el.tagName === 'SELECT') {
        Array.prototype.forEach.call(el.options, function (o) { if (o.value === value) el.value = value; });
      } else if (el.value === value) {
        el.checked = true;
      }
    });
  }

  /* ---------- Click tracking (secondary, never counted as leads) ---------- */
  function initClicks() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a, button');
      if (!a) return;
      var loc = a.getAttribute('data-mwl-loc') || '';
      var href = a.getAttribute('href') || '';

      if (a.hasAttribute('data-mwl-interest')) setChoice('interest', a.getAttribute('data-mwl-interest'));
      if (a.hasAttribute('data-mwl-insurance')) setChoice('insurance', a.getAttribute('data-mwl-insurance'));

      if (href.indexOf('tel:') === 0) track('click_to_call', { cta_location: loc });
      else if (a.getAttribute('data-mwl-track') === 'insurance') track('insurance_inquiry_click', { cta_location: loc });
      else if (a.getAttribute('data-mwl-track') === 'collection') track('collection_click', { cta_location: loc, link_type: a.getAttribute('data-mwl-link-type') });
      else if (a.getAttribute('data-mwl-track') === 'consult') track('consultation_cta_click', { cta_location: loc });
    });
  }

  /* ---------- Mobile sticky bar ---------- */
  function initMobileBar() {
    var bar = document.querySelector('[data-mwl-mobilebar]');
    var hero = document.querySelector('[data-mwl-hero]');
    var form = document.getElementById('consultation');
    if (!bar || !hero || !('IntersectionObserver' in window)) return;
    document.body.classList.add('has-mobilebar');
    var heroVisible = true, formVisible = false;
    function update() {
      var show = !heroVisible && !formVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.querySelectorAll('a').forEach(function (a) { a.tabIndex = show ? 0 : -1; });
    }
    new IntersectionObserver(function (en) { heroVisible = en[0].isIntersecting; update(); }).observe(hero);
    if (form) new IntersectionObserver(function (en) { formVisible = en[0].isIntersecting; update(); }).observe(form);
    update();
  }

  function init() {
    var attr = captureAttribution();
    document.querySelectorAll('form[data-mwl-lead-form]').forEach(function (f) { initForm(f, attr); });
    handleSuccess();
    initClicks();
    initMobileBar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
